import React, { useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import styled from 'styled-components/native'

import { BuiltEvent } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { Address } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { WordCloudButton } from '../../atoms/wordCloudButton/WordCloudButton'
import { GetEventsFn } from '../../hooks/useCalendarSlots'
import { CalendarClient, GetBookingDetailsForClient } from '../../ui-models/clients/CalendarClient'
import { tID } from '../../utils/utils'
import { ConfirmationModal } from '../confirmationModal/ConfirmationModal'
import {
  ProviderCalendarScheduleSessionContent,
  ProviderCalendarScheduleSessionContentRef,
} from '../providerCalendarScheduleSessionContent/ProviderCalendarScheduleSessionContent'
import {
  ProviderCalendarDateSlotSelectionContent,
  ProviderCalendarDateSlotSelectionContentRef,
} from '../providerCalendarSlotSelectionContent/ProviderCalendarDateSlotSelectionContent'

export type ProviderCalendarOpenSlotModalProps = {
  visible: boolean
  onRequestClose: () => void
  suggestedClients: CalendarClient[]
  clients: CalendarClient[]
  defaultVideoPlatform?: VIDEO_PLATFORM | null
  flexibleAvailabilityWindows?: boolean
  getEvents?: GetEventsFn
  splitVideoOptions?: boolean
  slotMaxTime?: string
  slotMinTime?: string
  rangeToAdd?: { start: string; end: string; startLocal: string; endLocal: string } | null
  onAddSlots?: (events: BuiltEvent[]) => void
  addresses?: Address[] | null
  inPersonPreference?: boolean
  showModalitySelection?: boolean
  videoPreference?: boolean
  onAddSession?: (event: {
    client: string
    startTime: string
    endTime: string
    slotEndTime: string
    duration: number
    meetingFormat: string
    episode?: Episode
    recurring: boolean
    frequency: number
    repeatFor: number
    address?: string
    videoPlatform?: VIDEO_PLATFORM
    sessionType?: string
  }) => Promise<void>
  getBookingDetailsForClient?: GetBookingDetailsForClient
  isEngageDAProvider?: boolean
  enableBackdatedAppointments?: boolean
  isSelfPayEnabled?: boolean
}

const Container = styled.View(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing['8px'],
  width: '100%',
}))

const Row = styled.View(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['16px'],
}))

enum OpenSlotType {
  SESSION = 'Session',
  ONE_TIME = 'One-time availability',
}

export const ProviderCalendarOpenSlotModal: React.FC<ProviderCalendarOpenSlotModalProps> = ({
  visible,
  onRequestClose,
  suggestedClients,
  clients,
  defaultVideoPlatform,
  flexibleAvailabilityWindows,
  getEvents,
  splitVideoOptions,
  slotMaxTime = '22:00:00',
  slotMinTime = '07:00:00',
  rangeToAdd,
  onAddSlots,
  onAddSession,
  addresses,
  inPersonPreference,
  showModalitySelection,
  videoPreference,
  getBookingDetailsForClient,
  isEngageDAProvider,
  enableBackdatedAppointments,
  isSelfPayEnabled,
}) => {
  const sessionContentRef = useRef<ProviderCalendarScheduleSessionContentRef>(null)
  const [openSlotType, setOpenSlotType] = useState<OpenSlotType>(OpenSlotType.SESSION)

  const [isSlotSelectionValid, setSlotSelectionValid] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const isConfirming = useRef(false)

  const oneTimeContentRef = useRef<ProviderCalendarDateSlotSelectionContentRef>(null)

  const handleAnimationEnd = () => {
    sessionContentRef.current?.reset()
    oneTimeContentRef.current?.reset()
    setOpenSlotType(OpenSlotType.SESSION)
    isConfirming.current = false
  }

  return (
    <ConfirmationModal
      visible={visible}
      onRequestClose={onRequestClose}
      handleAnimationEnd={handleAnimationEnd}
      onConfirmationButtonPress={async () => {
        if (isConfirming.current) {
          return
        }
        if (openSlotType === OpenSlotType.SESSION) {
          const values = sessionContentRef.current?.submit()
          if (values) {
            isConfirming.current = true
            setIsLoading(true)
            await onAddSession?.(values)
            setIsLoading(false)
            onRequestClose()
          }
        } else {
          const events = oneTimeContentRef.current?.buildPayload()
          if (events) {
            isConfirming.current = true
            onAddSlots?.(events)
            onRequestClose()
          }
        }
      }}
      modalTitle={
        <FormattedMessage defaultMessage='Schedule session' description='Title of modal to schedule a session' />
      }
      confirmationButtonText={
        openSlotType === OpenSlotType.SESSION ? (
          <FormattedMessage defaultMessage='Schedule' description='Button to schedule a session' />
        ) : (
          <FormattedMessage defaultMessage='Add' description='Button to add availability' />
        )
      }
      cancelButtonText={
        <FormattedMessage defaultMessage='Cancel' description='Button to cancel scheduling a session' />
      }
      confirmButtonDisabled={openSlotType === OpenSlotType.ONE_TIME && !isSlotSelectionValid}
      confirmButtonLoading={isLoading}
      testID={tID('ProviderCalendarOpenSlotModal')}
      modalContents={
        <Container>
          <Row>
            <WordCloudButton
              testID={tID('ProviderCalendarOpenSlotModal-session-button')}
              text={<FormattedMessage defaultMessage='Session' description='Button to schedule a session' />}
              isSelected={openSlotType === OpenSlotType.SESSION}
              onPress={() => setOpenSlotType(OpenSlotType.SESSION)}
            />
            <WordCloudButton
              testID={tID('ProviderCalendarOpenSlotModal-availability-button')}
              text={
                <FormattedMessage
                  defaultMessage='One-time availability'
                  description='Button to schedule one-time availability'
                />
              }
              isSelected={openSlotType === OpenSlotType.ONE_TIME}
              onPress={() => setOpenSlotType(OpenSlotType.ONE_TIME)}
            />
          </Row>
          {openSlotType === OpenSlotType.SESSION && (
            <ProviderCalendarScheduleSessionContent
              ref={sessionContentRef}
              suggestedClients={suggestedClients}
              clients={clients}
              getEvents={getEvents}
              slotMaxTime={slotMaxTime}
              slotMinTime={slotMinTime}
              rangeToAdd={rangeToAdd}
              getBookingDetailsForClient={getBookingDetailsForClient}
              bookableInPersonLocations={isEngageDAProvider ? addresses : null}
              enableBackdatedAppointments={enableBackdatedAppointments}
              isSelfPayEnabled={isSelfPayEnabled}
              splitVideoOptions={splitVideoOptions}
              providerVideoPlatform={defaultVideoPlatform}
              videoPreference={videoPreference}
              inPersonPreference={inPersonPreference}
            />
          )}
          {openSlotType === OpenSlotType.ONE_TIME && (
            <ProviderCalendarDateSlotSelectionContent
              ref={oneTimeContentRef}
              addresses={addresses}
              defaultVideoPlatform={defaultVideoPlatform}
              flexibleAvailabilityWindows={flexibleAvailabilityWindows}
              getEvents={getEvents}
              inPersonPreference={inPersonPreference}
              splitVideoOptions={splitVideoOptions}
              onValidityChange={setSlotSelectionValid}
              rangeToAdd={rangeToAdd}
              showModalitySelection={showModalitySelection}
              slotMaxTime={slotMaxTime}
              slotMinTime={slotMinTime}
              videoPreference={videoPreference}
            />
          )}
        </Container>
      }
    />
  )
}
